// Pure helpers over the normalized tables: hierarchy walking, status inheritance,
// custom-field scoping and location membership.

import type { CustomField, Folder, List, Location, Space, Status, Task } from '@shared/types';
import { isCompleteType } from '@shared/constants';
import type { Tables } from './store';

export function byOrder<T extends { orderIndex: number }>(a: T, b: T) {
  return a.orderIndex - b.orderIndex;
}

export function spacesOf(db: Tables, workspaceId: string): Space[] {
  return Object.values(db.spaces)
    .filter((s) => s.workspaceId === workspaceId && !s.archived)
    .sort(byOrder);
}

export function foldersOf(db: Tables, spaceId: string): Folder[] {
  return Object.values(db.folders)
    .filter((f) => f.spaceId === spaceId && !f.archived)
    .sort(byOrder);
}

export function listsOf(db: Tables, spaceId: string, folderId: string | null): List[] {
  return Object.values(db.lists)
    .filter((l) => l.spaceId === spaceId && (l.folderId ?? null) === folderId && !l.archived)
    .sort(byOrder);
}

export function effectiveStatuses(db: Tables, listId: string): Status[] {
  const list = db.lists[listId];
  if (!list) return [];
  if (list.statuses?.length) return [...list.statuses].sort(byOrder);
  const folder = list.folderId ? db.folders[list.folderId] : null;
  if (folder?.statuses?.length) return [...folder.statuses].sort(byOrder);
  return [...(db.spaces[list.spaceId]?.statuses ?? [])].sort(byOrder);
}

export function statusOf(db: Tables, task: Task): Status {
  const statuses = effectiveStatuses(db, task.listId);
  return (
    statuses.find((s) => s.id === task.statusId) ??
    statuses[0] ?? { id: task.statusId, name: 'unknown', color: '#87909e', type: 'open', orderIndex: 0 }
  );
}

export function isTaskClosed(db: Tables, task: Task) {
  return isCompleteType(statusOf(db, task).type);
}

/** Lists contained in a location (recursively). */
export function listIdsIn(db: Tables, loc: Location): Set<string> {
  const ids = new Set<string>();
  for (const l of Object.values(db.lists)) {
    if (l.archived) continue;
    if (loc.type === 'list' ? l.id === loc.id : loc.type === 'folder' ? l.folderId === loc.id : loc.type === 'space' ? l.spaceId === loc.id : db.spaces[l.spaceId]?.workspaceId === loc.id) {
      ids.add(l.id);
    }
  }
  return ids;
}

export function tasksIn(db: Tables, loc: Location): Task[] {
  const lists = listIdsIn(db, loc);
  return Object.values(db.tasks).filter((t) => lists.has(t.listId) && !t.archived);
}

export function workspaceOfLocation(db: Tables, loc: Location): string | undefined {
  if (loc.type === 'workspace') return loc.id;
  if (loc.type === 'space') return db.spaces[loc.id]?.workspaceId;
  if (loc.type === 'folder') return db.spaces[db.folders[loc.id]?.spaceId]?.workspaceId;
  return db.spaces[db.lists[loc.id]?.spaceId]?.workspaceId;
}

export function spaceIdOfLocation(db: Tables, loc: Location): string | undefined {
  if (loc.type === 'space') return loc.id;
  if (loc.type === 'folder') return db.folders[loc.id]?.spaceId;
  if (loc.type === 'list') return db.lists[loc.id]?.spaceId;
  return undefined;
}

export function locationName(db: Tables, loc: Location): string {
  if (loc.type === 'workspace') return db.workspaces[loc.id]?.name ?? 'Workspace';
  if (loc.type === 'space') return db.spaces[loc.id]?.name ?? 'Space';
  if (loc.type === 'folder') return db.folders[loc.id]?.name ?? 'Folder';
  return db.lists[loc.id]?.name ?? 'List';
}

/** Breadcrumb from space down to the location. */
export function breadcrumb(db: Tables, loc: Location): Location[] {
  const out: Location[] = [];
  if (loc.type === 'list') {
    const l = db.lists[loc.id];
    if (!l) return [loc];
    out.push({ type: 'space', id: l.spaceId });
    if (l.folderId) out.push({ type: 'folder', id: l.folderId });
    out.push(loc);
  } else if (loc.type === 'folder') {
    const f = db.folders[loc.id];
    if (f) out.push({ type: 'space', id: f.spaceId });
    out.push(loc);
  } else out.push(loc);
  return out;
}

/** Custom fields visible on a list (workspace, space, folder and list scoped). */
export function fieldsForList(db: Tables, listId: string): CustomField[] {
  const l = db.lists[listId];
  if (!l) return [];
  const ws = db.spaces[l.spaceId]?.workspaceId;
  return Object.values(db.customFields)
    .filter(
      (f) =>
        (f.scope.type === 'workspace' && f.scope.id === ws) ||
        (f.scope.type === 'space' && f.scope.id === l.spaceId) ||
        (f.scope.type === 'folder' && f.scope.id === l.folderId) ||
        (f.scope.type === 'list' && f.scope.id === l.id),
    )
    .sort(byOrder);
}

/** Custom fields usable as columns for any list within a location. */
export function fieldsForLocation(db: Tables, loc: Location): CustomField[] {
  const seen = new Map<string, CustomField>();
  for (const listId of listIdsIn(db, loc)) for (const f of fieldsForList(db, listId)) seen.set(f.id, f);
  return [...seen.values()].sort(byOrder);
}

/** Status set shown for a location: exact for a list/folder, merged by name otherwise. */
export function statusesForLocation(db: Tables, loc: Location): Status[] {
  if (loc.type === 'list') return effectiveStatuses(db, loc.id);
  const byName = new Map<string, Status>();
  for (const listId of listIdsIn(db, loc)) {
    for (const s of effectiveStatuses(db, listId)) {
      const key = s.name.toLowerCase();
      if (!byName.has(key)) byName.set(key, s);
    }
  }
  const typeRank = { open: 0, unstarted: 1, custom: 2, done: 3, closed: 4 } as const;
  return [...byName.values()].sort((a, b) => typeRank[a.type] - typeRank[b.type] || a.orderIndex - b.orderIndex);
}

export function subtasksOf(db: Tables, taskId: string): Task[] {
  return Object.values(db.tasks)
    .filter((t) => t.parentId === taskId && !t.archived)
    .sort(byOrder);
}

export function taskPath(db: Tables, task: Task): Task[] {
  const chain: Task[] = [];
  let cur = task.parentId ? db.tasks[task.parentId] : undefined;
  while (cur && chain.length < 20) {
    chain.unshift(cur);
    cur = cur.parentId ? db.tasks[cur.parentId] : undefined;
  }
  return chain;
}

export function locationKey(loc: Location) {
  return `${loc.type}:${loc.id}`;
}

export function sameLocation(a: Location, b: Location) {
  return a.type === b.type && a.id === b.id;
}

export function userName(db: Tables, id: string | null | undefined) {
  return (id && db.users[id]?.name) || 'Unknown';
}

export function initials(name: string) {
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] ?? '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}
